import { useState, useEffect, useRef } from "react";
import { Star, ChevronLeft, ChevronRight, MessageCircle, Quote } from "lucide-react";
import httpClient from "../Api/axios";

export default function ScrollableReviewGrid() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [expandedReviewId, setExpandedReviewId] = useState(null);
  const scrollContainerRef = useRef(null);
  
  // Fetch reviews
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setLoading(true);
        const response = await httpClient.get("/reviews");
        // Add an id to each review if it doesn't already have one
        const reviewsWithIds = response.data.map((review, index) => ({
          ...review,
          id: review.id || index
        }));
        setReviews(reviewsWithIds);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching reviews:", err);
        setError("Unable to load reviews at this time");
        setLoading(false);
      }
    };
    fetchReviews();
  }, []);

  // Handle scroll navigation
  const scrollBy = (direction) => {
    if (!scrollContainerRef.current) return;
    
    const container = scrollContainerRef.current;
    const scrollAmount = direction === 'right' 
      ? container.clientWidth * 0.75 
      : -container.clientWidth * 0.75;
    
    container.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });
  };

  // Track scroll position for button visibility
  const handleScroll = () => {
    if (!scrollContainerRef.current) return;
    setScrollPosition(scrollContainerRef.current.scrollLeft);
  };

  // Toggle expanded review
  const toggleExpandReview = (id) => {
    if (expandedReviewId === id) {
      setExpandedReviewId(null);
    } else {
      setExpandedReviewId(id);
    }
  };

  // Display star rating
  const StarRating = ({ rating }) => (
    <div className="flex space-x-1 mb-2">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          size={16}
          className={`${
            i < rating ? "text-yellow-400 fill-yellow-400" : "text-gray-300"
          }`}
        />
      ))}
    </div>
  );

  // Loading state
  if (loading) {
    return (
      <section className="bg-gradient-to-b from-purple-100 to-white py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8 text-purple-900">Customer Reviews</h2>
          <div className="flex justify-center">
            <div className="h-10 w-10 rounded-full border-4 border-purple-500 border-t-transparent animate-spin"></div>
          </div>
        </div>
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section className="bg-gradient-to-b from-purple-100 to-white py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8 text-purple-900">Customer Reviews</h2>
          <div className="bg-red-50 p-4 rounded-lg max-w-md mx-auto text-center">
            <p className="text-red-600">{error}</p>
            <button 
              className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
              onClick={() => window.location.reload()}
            >
              Try Again
            </button>
          </div>
        </div>
      </section>
    );
  }

  // Empty state
  if (!reviews.length) {
    return (
      <section className="bg-gradient-to-b from-purple-100 to-white py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8 text-purple-900">Customer Reviews</h2>
          <div className="bg-white shadow p-6 rounded-lg max-w-md mx-auto text-center">
            <MessageCircle size={28} className="mx-auto mb-3 text-gray-400" />
            <p className="text-gray-600">No reviews available yet</p>
          </div>
        </div>
      </section>
    );
  }

  // Max scroll position calculation
  const isAtStart = scrollPosition === 0;
  const isAtEnd = scrollContainerRef.current 
    ? scrollPosition >= scrollContainerRef.current.scrollWidth - scrollContainerRef.current.clientWidth - 10
    : false;

  return (
    <section className="bg-gradient-to-b from-purple-50 to-white py-16 mt-20 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            What Our Patients Are Saying
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Real feedback from customers who have experienced our products and services
          </p>
        </div>

        {/* Reviews Container with Controls */}
        <div className="relative">
          {/* Scroll navigation buttons */}
          <button 
            onClick={() => scrollBy('left')}
            disabled={isAtStart}
            className={`absolute left-0 top-1/2 -translate-y-1/2 -ml-4 z-10 bg-white rounded-full shadow-lg p-2 md:p-3 ${
              isAtStart ? 'opacity-30 cursor-not-allowed' : 'hover:bg-purple-50'
            }`}
            aria-label="Scroll left"
          >
            <ChevronLeft size={24} className="text-purple-700" />
          </button>

          {/* Scrollable container */}
          <div 
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="flex overflow-x-auto pb-8 gap-4 md:gap-6 hide-scrollbar snap-x snap-mandatory"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {reviews.map((review, index) => (
              <div 
                key={index}
                className="snap-start flex-none w-4/5 sm:w-1/3 md:w-1/4 lg:w-1/4"
              >
                <div className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow h-full p-6 border border-purple-100">
                  <div className="flex items-start mb-4">
                    <div className="bg-gradient-to-b from-sky-700 to-sky-800 rounded-full h-10 w-10 flex items-center justify-center text-white font-bold flex-shrink-0">
                      {review.name.charAt(0)}
                    </div>
                    <div className="ml-3">
                      <h3 className="font-medium text-gray-900">{review.name}</h3>
                      <p className="text-sm text-gray-500">{review.status}</p>
                    </div>
                  </div>
                  
                  <StarRating rating={review.star_count} />
                  
                  <div className="relative">
                    <p className={`text-gray-700 leading-relaxed ${expandedReviewId === review.id ? "" : "line-clamp-4"}`}>
                      {review.review}
                    </p>
                    
                    {/* Only show gradient and button if review is not expanded */}
                    {expandedReviewId !== review.id && (
                      <>
                        {/* No gradient fade at the bottom anymore */}
                        <button
                          onClick={() => toggleExpandReview(review.id)}
                          className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors duration-200"
                        >
                          See Full Review
                        </button>
                      </>
                    )}
                    
                    {/* Show "Show Less" button if expanded */}
                    {expandedReviewId === review.id && (
                      <button
                        onClick={() => toggleExpandReview(review.id)}
                        className="mt-2 text-purple-600 hover:text-purple-800 text-sm font-medium transition-colors duration-200"
                      >
                        Show Less
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button 
            onClick={() => scrollBy('right')}
            disabled={isAtEnd}
            className={`absolute right-0 top-1/2 -translate-y-1/2 -mr-4 z-10 bg-white rounded-full shadow-lg p-2 md:p-3 ${
              isAtEnd ? 'opacity-30 cursor-not-allowed' : 'hover:bg-purple-50'
            }`}
            aria-label="Scroll right"
          >
            <ChevronRight size={24} className="text-purple-700" />
          </button>
        </div>

        {/* Scroll indicator */}
        <div className="flex justify-center mt-6 space-x-1">
          {Array.from({ length: Math.ceil(reviews.length / 4) }).map((_, idx) => {
            // Calculate which indicator should be active
            const containerWidth = scrollContainerRef.current?.clientWidth || 0;
            const totalWidth = scrollContainerRef.current?.scrollWidth || 0;
            const maxScrollPosition = totalWidth - containerWidth;
            
            // Determine which segment of the scroll we're in
            const segmentSize = maxScrollPosition / Math.max(Math.ceil(reviews.length / 4) - 1, 1);
            const activeSegment = segmentSize > 0 
              ? Math.min(Math.floor(scrollPosition / segmentSize), Math.ceil(reviews.length / 4) - 1) 
              : 0;
            
            return (
              <button
                key={idx}
                className={`h-2 rounded-full transition-all duration-300 ${
                  idx === activeSegment ? "w-6 bg-purple-600" : "w-2 bg-gray-300"
                }`}
                onClick={() => {
                  if (!scrollContainerRef.current) return;
                  
                  const targetPosition = segmentSize * idx;
                  scrollContainerRef.current.scrollTo({
                    left: targetPosition,
                    behavior: 'smooth'
                  });
                }}
                aria-label={`Scroll to section ${idx + 1}`}
              />
            );
          })}
        </div>
      </div>

      {/* Custom CSS to hide scrollbars */}
      <style jsx>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </section>
  );
}